import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarClock, CheckCircle2, ShieldAlert, Wrench } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ExtensionApprovals = ({ token, onCount }) => {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [reqs, setReqs] = useState([]);

  const load = useCallback(() => {
    axios.get(`${API}/extension-requests?status=pending`, auth)
      .then((r) => { setReqs(r.data || []); onCount?.((r.data || []).length); })
      .catch(() => {});
    // eslint-disable-next-line
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const approve = async (rq, force = false) => {
    try {
      const { data } = await axios.post(`${API}/extension-requests/${rq.id}/approve?force=${force}`, {}, auth);
      if (data.reconciled) { toast.success(data.message || "Request reconciled — the extension was already applied. No new charge."); load(); return; }
      const finalDate = data.extension?.new_return_date || rq.effective_new_return_date || rq.new_return_date;
      toast.success(`Extension approved — ${rq.name} keeps the ${rq.vehicle} until ${finalDate}.${data.date_recomputed ? " (Dates were recomputed from the current return date.)" : ""}${data.agreement_auto_signed ? " Agreement auto-signed & emailed." : ""}`);
      load();
    } catch (e) {
      if (e?.response?.status === 423) {
        if (window.confirm(`🛑 ${e.response.data?.detail}\n\nHave you reviewed the booking's insurance and protection settings? Click OK to approve anyway.`)) return approveWithAck(rq, force);
        return;
      }
      if (e?.response?.status === 409 && !force) {
        if (window.confirm(`⚠ ${e.response.data?.detail || "This extension overlaps another reservation."}`)) return approve(rq, true);
        return;
      }
      toast.error(e?.response?.data?.detail || "Could not approve the extension.");
    }
  };

  const approveWithAck = async (rq, force = false) => {
    try {
      const { data } = await axios.post(`${API}/extension-requests/${rq.id}/approve?force=${force}&coverage_ack=true&insurance_ack=true`, {}, auth);
      if (data.reconciled) { toast.success(data.message || "Request reconciled — the extension was already applied. No new charge."); load(); return; }
      const finalDate = data.extension?.new_return_date || rq.effective_new_return_date || rq.new_return_date;
      toast.success(`Extension approved — ${rq.name} keeps the ${rq.vehicle} until ${finalDate}.${data.date_recomputed ? " (Dates were recomputed from the current return date.)" : ""}${data.agreement_auto_signed ? " Agreement auto-signed & emailed." : ""}`);
      load();
    } catch (e) {
      if (e?.response?.status === 409 && !force) {
        if (window.confirm(`⚠ ${e.response.data?.detail || "This extension overlaps another reservation."}`)) return approveWithAck(rq, true);
        return;
      }
      toast.error(e?.response?.data?.detail || "Could not approve the extension.");
    }
  };

  const fixCoverage = (rq) => {
    window.dispatchEvent(new CustomEvent("autohire:open-booking", { detail: rq.booking_id }));
    toast.info("Opening the booking — fix the insurance under Pricing Breakdown → Insurance, then come back to approve.");
  };

  const decline = async (rq) => {
    const reason = window.prompt(`Decline ${rq.name}'s extension request? Optional reason (sent to the customer):`, "");
    if (reason === null) return;
    try {
      await axios.post(`${API}/extension-requests/${rq.id}/decline`, { reason }, auth);
      toast.success("Extension request declined — customer notified.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not decline the request."); }
  };

  if (reqs.length === 0) return null;
  return (
    <div data-testid="extension-approvals-panel" className="rounded-xl border border-sky-400/40 bg-sky-400/[0.05] p-5">
      <h3 className="flex items-center gap-2 font-display font-bold text-lg mb-1 text-sky-300">
        <CalendarClock size={18} /> Extension Requests — Pending Your Approval
        <span className="px-2 py-0.5 rounded-full bg-red-500 text-white text-xs font-bold">{reqs.length}</span>
      </h3>
      <p className="text-white/50 text-xs mb-3">Customers asking for more time on their current rental. Approve in one tap — the extension bills automatically and the agreement is auto-signed.</p>
      <div className="space-y-2">
        {reqs.map((rq) => (
          <div key={rq.id} data-testid={`ext-approval-${rq.id}`} className={`bg-[#0A0A0A] border rounded-lg px-4 py-3 ${rq.coverage_mismatch ? "border-red-400/50" : "border-white/10"}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="font-semibold truncate">{rq.name} <span className="text-white/40 font-normal">· {rq.vehicle}</span></div>
              <div className="text-white/45 text-xs mt-0.5">
                +{rq.days} day{rq.days !== 1 ? "s" : ""}: {rq.effective_prev_return_date || rq.prev_return_date} → <span data-testid={`ext-approval-effective-date-${rq.id}`} className="text-sky-300 font-semibold">{rq.effective_new_return_date || rq.new_return_date}</span>
                {rq.stale && (
                  <span data-testid={`ext-approval-stale-${rq.id}`} title={`Originally requested ${rq.prev_return_date} → ${rq.new_return_date} — the booking's return date has since moved. Approving now will extend from the CURRENT return date instead, for the same ${rq.days} day(s).`}
                    className="ml-2 text-[10px] font-bold text-amber-300 border border-amber-400/40 rounded px-1.5 py-0.5 uppercase tracking-wide">
                    Dates updated (was → {rq.new_return_date})
                  </span>
                )}
                <span className="text-[#F2B705] font-semibold ml-2">est. ${Number(rq.estimated_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                {rq.note && <span className="block text-white/35 italic mt-0.5">"{rq.note}"</span>}
              </div>
            </div>
            <div className="flex gap-2">
              <button data-testid={`ext-approval-approve-${rq.id}`} onClick={() => approve(rq)}
                className="flex items-center gap-1.5 text-xs font-semibold border border-green-400/50 text-green-300 px-3 py-1.5 rounded hover:bg-green-400 hover:text-black transition-colors">
                <CheckCircle2 size={13} /> Approve
              </button>
              <button data-testid={`ext-approval-decline-${rq.id}`} onClick={() => decline(rq)}
                className="text-xs font-semibold border border-red-400/40 text-red-300 px-3 py-1.5 rounded hover:bg-red-400 hover:text-black transition-colors">
                Decline
              </button>
            </div>
            </div>
            {rq.coverage_mismatch && (
              <div data-testid={`ext-coverage-mismatch-${rq.id}`} className="mt-3 rounded-lg border border-red-400/40 bg-red-500/[0.07] p-3">
                <p className="flex items-start gap-2 text-red-300 text-xs font-bold leading-relaxed">
                  <ShieldAlert size={15} className="shrink-0 mt-0.5" />
                  Coverage mismatch detected. Review the booking's current insurance and protection settings before approving this extension.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-x-4 gap-y-1.5 mt-2.5 text-[11px]">
                  <div><span className="block text-white/40 uppercase tracking-wide text-[10px]">Coverage Type</span><span className="text-white/85 font-semibold">{rq.coverage_mismatch.coverage_type}</span></div>
                  <div><span className="block text-white/40 uppercase tracking-wide text-[10px]">Currently Charged</span><span className="text-red-300 font-semibold">${Number(rq.coverage_mismatch.current_rate || 0).toFixed(2)}/day</span></div>
                  <div><span className="block text-white/40 uppercase tracking-wide text-[10px]">Expected Rate</span><span className="text-white/85 font-semibold">${Number(rq.coverage_mismatch.expected_rate || 0).toFixed(2)}/day</span></div>
                  <div><span className="block text-white/40 uppercase tracking-wide text-[10px]">Difference</span><span className="text-[#F2B705] font-semibold">${Number(rq.coverage_mismatch.difference || 0).toFixed(2)}/day</span></div>
                  <div><span className="block text-white/40 uppercase tracking-wide text-[10px]">Detected</span><span className="text-white/85 font-semibold">{(rq.coverage_mismatch.detected_at || "").slice(0, 10) || "—"}</span></div>
                </div>
                <p className="text-white/45 text-[11px] mt-2">Approval is blocked until management reviews the mismatch — the extension will not be priced or approved automatically.</p>
                <button data-testid={`ext-fix-coverage-${rq.id}`} onClick={() => fixCoverage(rq)}
                  className="mt-2 flex items-center gap-1.5 text-xs font-semibold border border-[#F2B705]/60 text-[#F2B705] px-3 py-1.5 rounded hover:bg-[#F2B705] hover:text-black transition-colors">
                  <Wrench size={13} /> Fix Coverage
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ExtensionApprovals;
